import React from "react";
import { cn } from "@/lib/utils";

/**
 * Status / payment pills.
 *
 * Each pill uses a base palette that's readable on the light surface, plus a
 * `dark:` variant that swaps to a darkened backdrop with a brighter foreground
 * so the pill stays legible against the dark `pp-surface`. The pulsing
 * `in_progress` dot stays orange in both modes.
 */
const COLORS = {
  // Outlined slate pill — neutral in light; muted outline + muted text in dark.
  payment_pending:
    "border border-pp-text-muted text-pp-text-muted",
  // Solid brand orange in both modes.
  accepted:
    "bg-brand-orange text-white",
  // Solid brand orange in both modes (the dot adds the live cue).
  in_progress:
    "bg-brand-orange text-white",
  // Muted slate — fades in dark mode using pp-surface-2 + muted text.
  completed:
    "bg-pp-surface2 text-pp-text-muted dark:bg-pp-surface2 dark:text-pp-text-muted",
  // Muted red wash — soft in light, deeper in dark.
  cancelled:
    "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
};
const LABELS = {
  payment_pending: "Awaiting payment",
  accepted: "Accepted",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

export function BookingStatusPill({ status }) {
  const inProgress = status === "in_progress";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider",
        COLORS[status] || "bg-muted text-muted-foreground"
      )}
      data-testid={`status-pill-${status}`}
    >
      {inProgress && <span className="pp-live-dot" aria-hidden="true" />}
      {LABELS[status] || status}
    </span>
  );
}

export function RecurringTag({ scheduleType }) {
  if (scheduleType === "one_time") return null;
  const label =
    scheduleType === "recurring_weekly" ? "Weekly" : "Twice weekly";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-brand-orange/10 px-2 py-0.5 text-xs font-bold text-brand-orange"
      data-testid="recurring-tag"
    >
      🔁 {label}
    </span>
  );
}

export function PaymentBadge({ paid }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.7rem] font-bold uppercase tracking-wider",
        paid
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          : "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
      )}
      data-testid={`payment-badge-${paid ? "paid" : "unpaid"}`}
    >
      {paid ? "Paid" : "Unpaid"}
    </span>
  );
}

/**
 * PriceBreakdown — itemized invoice rendered from `pricing_snapshot`.
 *
 * Pure per-dog refactor:
 *   1. `$X / dog × N dogs`
 *   2. Securement fee (× M dogs)        — only if M > 0
 *   3. Subtotal
 *   4. Recurring discount (P%)           — only if P > 0  (negative line)
 *   5. Total (bold)
 *
 * Processing-fee inflation (snapshot-bound):
 *   When `snapshot.processing_fee_enabled` is true and `base_total` exists,
 *   we proportionally rebase each non-discount line by `total / base_total`
 *   so the breakdown sums to the inflated total the customer actually pays.
 *   No discrete "processing fee" line is rendered — the fee is bundled into
 *   the line items. This is intentional per product spec: customers see an
 *   ALL-IN price, not a separate add-on.
 *
 * Backward compat: legacy snapshots that still have a `base_price > 0` show
 * an extra "Per-visit base fee" line at the top so old invoices render
 * faithfully. New snapshots never include `base_price`, so the legacy line
 * is hidden. Snapshots from before the processing-fee phase have neither
 * `base_total` nor `processing_fee_enabled` — they fall through the legacy
 * path (ratio = 1) and render identically to before.
 */
export function PriceBreakdown({
  snapshot,
  dogCount = 0,
  unsecuredCount = 0,
  compact = false,
}) {
  if (!snapshot) return null;
  const s = snapshot;
  const perDog = s.per_dog_price || 0;
  // legacy: keep rendering historical invoices with the old base_price line.
  const legacyBase = s.base_price && s.base_price > 0 ? s.base_price : 0;

  // Proportional rebase ratio. When fee is on AND base_total > 0, scale
  // each line item so the breakdown adds up to the inflated total. When
  // fee is off (or snapshot predates the phase), ratio is 1 and the math
  // is identical to the pre-phase behavior.
  const feeOn = s.processing_fee_enabled === true && (s.base_total || 0) > 0;
  const ratio = feeOn ? (s.total || 0) / s.base_total : 1;

  // Phase C — First-Clean Fee line item. Three visual states, driven
  // by the (already-frozen) snapshot fields:
  //   1. `is_first_booking === false` OR fee_cents === 0 → hidden.
  //   2. `is_first_booking === true` + `first_clean_fee_waived === true`
  //      → strikethrough anchor with "Waived — launch special" tag,
  //      contributes $0 to total.
  //   3. `is_first_booking === true` + `first_clean_fee_waived === false`
  //      → normal line item at the anchor amount, contributes to total.
  // The FE reads the anchor from `snapshot.first_clean_fee_cents` (frozen
  // at booking creation), NOT from a hardcoded literal — this is the
  // whole point of the integrity guard in Family E.
  const firstCleanAnchorCents = s.first_clean_fee_cents || 0;
  const firstCleanChargeCents = s.first_clean_fee_charge_cents || 0;
  const showFirstCleanRow =
    s.is_first_booking === true && firstCleanAnchorCents > 0;
  const firstCleanWaived = s.first_clean_fee_waived === true;

  return (
    <div
      className={cn("space-y-1 text-sm", compact && "text-xs")}
      data-testid="price-breakdown"
    >
      {legacyBase > 0 && (
        <Row
          label="Per-visit base fee (legacy)"
          amount={legacyBase * ratio}
          testid="price-breakdown-legacy-base"
        />
      )}
      <Row
        label={`$${(perDog * ratio).toFixed(2)} / dog × ${dogCount} ${dogCount === 1 ? "dog" : "dogs"}`}
        amount={perDog * dogCount * ratio}
        testid="price-breakdown-per-dog"
      />
      {unsecuredCount > 0 && (
        <Row
          label={`Securement fee (× ${unsecuredCount} ${unsecuredCount === 1 ? "dog" : "dogs"})`}
          amount={(s.securement_fee_per_dog || 0) * unsecuredCount * ratio}
          testid="price-breakdown-securement"
        />
      )}
      <Row label="Subtotal" amount={(s.subtotal || 0) * ratio} bold testid="price-breakdown-subtotal" />
      {s.recurring_discount_amount > 0 && (
        <Row
          label={`Recurring discount (${s.recurring_discount_pct}%)`}
          amount={-(s.recurring_discount_amount || 0) * ratio}
          testid="price-breakdown-discount"
        />
      )}
      {showFirstCleanRow && (
        <FirstCleanFeeRow
          anchorCents={firstCleanAnchorCents}
          chargeCents={firstCleanChargeCents}
          waived={firstCleanWaived}
        />
      )}
      {(s.member_discount_absorbed_cents || 0) > 0 && (
        <MemberDiscountRow
          absorbedCents={s.member_discount_absorbed_cents || 0}
          clamped={s.member_discount_clamped === true}
          tierName={s.member_discount_tier_name || ""}
        />
      )}
      <Row label="Total" amount={s.total} bold large testid="price-breakdown-total" />
    </div>
  );
}

/**
 * FirstCleanFeeRow — dedicated component for the Phase C promotional
 * line item. Three visual states driven entirely by props:
 *   - `waived=true`  → strikethrough anchor + "Waived — launch special"
 *                      tag, contributes $0 to the running total.
 *   - `waived=false` → normal line item at `anchorCents/100`, contributes
 *                      to the running total.
 *   - (hidden case)  → this component is NOT rendered by the parent;
 *                      the visibility gate lives in `PriceBreakdown`.
 *
 * Data-testid contract:
 *   `price-breakdown-first-clean-fee`         → row wrapper
 *   `price-breakdown-first-clean-fee-anchor`  → strikethrough $ span (waived)
 *   `price-breakdown-first-clean-fee-tag`     → "Waived …" pill (waived)
 *   `price-breakdown-first-clean-fee-charge`  → $ span (charged path)
 */
function FirstCleanFeeRow({ anchorCents, chargeCents, waived }) {
  const anchorDollars = (anchorCents || 0) / 100;
  const chargeDollars = (chargeCents || 0) / 100;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2"
      data-testid="price-breakdown-first-clean-fee"
    >
      <span className="text-muted-foreground flex flex-wrap items-center gap-2">
        <span>First-clean fee</span>
        {waived && (
          <span
            className="inline-flex items-center rounded-full bg-brand-orange/10 px-2 py-0.5 text-[0.7rem] font-bold uppercase tracking-wider text-brand-orange"
            data-testid="price-breakdown-first-clean-fee-tag"
          >
            Waived — launch special
          </span>
        )}
      </span>
      {waived ? (
        <span
          className="line-through text-muted-foreground/70"
          data-testid="price-breakdown-first-clean-fee-anchor"
        >
          {`$${anchorDollars.toFixed(2)}`}
        </span>
      ) : (
        <span data-testid="price-breakdown-first-clean-fee-charge">
          {`$${chargeDollars.toFixed(2)}`}
        </span>
      )}
    </div>
  );
}

/**
 * MemberDiscountRow — Phase E platform-funded discount line item.
 *
 * Renders ONLY when the customer actually got money off — i.e. when
 * `snapshot.member_discount_absorbed_cents > 0`. Reads amount + tier
 * name + clamp flag STRAIGHT FROM THE SNAPSHOT — no hardcoded literals,
 * no local calculation. This is the whole point of the Family E
 * integrity guard (no `10` / `17` / `0.1` / `0.17` in this component).
 *
 * Three visual states:
 *   - `clamped=false`, `tierName="Plus"`  → `Member discount — Plus`
 *     with a negative dollar amount styled as a discount.
 *   - `clamped=true`                       → small info icon after the
 *     label with the tooltip copy "Discount capped at partner's
 *     commission tier." (brand-cheerful).
 *   - `absorbedCents=0`                    → NOT rendered (caller
 *     gates the visibility).
 */
function MemberDiscountRow({ absorbedCents, clamped, tierName }) {
  const absorbedDollars = (absorbedCents || 0) / 100;
  const label = tierName
    ? `Member discount — ${tierName}`
    : "Member discount";
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2"
      data-testid="price-breakdown-member-discount"
    >
      <span
        className="text-muted-foreground flex flex-wrap items-center gap-2"
        data-testid="price-breakdown-member-discount-label"
      >
        <span>{label}</span>
        {clamped && (
          <span
            className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[0.65rem] font-bold text-muted-foreground"
            role="img"
            aria-label="Discount capped at partner's commission tier."
            title="Discount capped at partner's commission tier."
            data-testid="price-breakdown-member-discount-clamp-icon"
          >
            i
          </span>
        )}
      </span>
      <span
        className="text-brand-orange font-semibold"
        data-testid="price-breakdown-member-discount-amount"
      >
        {`−$${absorbedDollars.toFixed(2)}`}
      </span>
    </div>
  );
}


function Row({ label, amount, bold, large, testid }) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-2",
        bold && "font-semibold text-pp-text",
        large && "text-base"
      )}
      data-testid={testid}
    >
      <span className={!bold ? "text-muted-foreground" : ""}>{label}</span>
      <span>{amount < 0 ? `−$${Math.abs(amount).toFixed(2)}` : `$${(amount || 0).toFixed(2)}`}</span>
    </div>
  );
}
